import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useActionState, useState } from "react";
import { HeldTitle, PagePending } from "@/client/components/page-pending";
import { api, keyed, unwrap } from "@/client/lib/api";
import { clientsQuery } from "@/client/lib/clients-query";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { problemFor } from "@/client/lib/problems";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import { fail } from "@/client/lib/toast";
import { useNow } from "@/client/lib/use-now";
import { dayIn } from "@/features/attendance/rules";
import {
  type FilterName,
  InvoiceFilters,
} from "@/features/invoices/components/invoice-filters";
import { InvoiceTableSkeleton } from "@/features/invoices/components/invoice-table";
import {
  InvoicesHeading,
  InvoicesPage,
  InvoicesToolbar,
  NewInvoiceForm,
} from "@/features/invoices/components/invoices-page";
import { fiscalYearOf } from "@/features/invoices/invoice-list";
import {
  type InvoiceDetail,
  invoiceQuery,
  invoiceSettingsQuery,
  newDrafts,
} from "@/features/invoices/invoice-query";
import {
  type InvoiceListRow,
  invoicesQuery,
} from "@/features/invoices/invoices-query";
import {
  type InvoiceListSearch,
  invoiceListSearch,
} from "@/features/invoices/list-search";

/** Overdue turns over at the company's midnight, so the list re-reads the clock each minute. */
const TICK = 60_000;

export const Route = createFileRoute("/_authenticated/$slug/invoices/")({
  validateSearch: invoiceListSearch,
  // An Admin's and a Manager's; the server refuses an Employee too.
  beforeLoad: ({ context }) => {
    requireModule(context, "invoices");
    if (context.role === "employee") {
      throw redirect({ to: "/$slug", params: { slug: context.companySlug } });
    }
  },
  loader: async ({ context }) => {
    await Promise.all([
      ensureTenantData(context.queryClient, invoicesQuery),
      ensureTenantData(context.queryClient, clientsQuery),
      ensureTenantData(context.queryClient, invoiceSettingsQuery),
    ]);
  },
  head: async ({ match }) => {
    const { invoicesCopy } = await import("@/features/invoices/copy");
    return {
      meta: [
        {
          title: pageTitle(invoicesCopy.list.title, match.context.companyName),
        },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: InvoicesPending,
  component: Invoices,
});

/**
 * The list's first load: the title, the filters from the address, the search and New invoice drawn
 * for real; the owed line waits on the invoices, and a chip whose label waits on what is not read.
 */
function InvoicesPending() {
  const search = Route.useSearch();
  const { companyTimezone } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<FilterName | null>(null);
  const [create, creating] = useNewInvoice();
  const onSearch = useSearchChange();
  const settings = queryClient.getQueryData(invoiceSettingsQuery.queryKey);
  const clients = queryClient.getQueryData(clientsQuery.queryKey);
  const india = settings?.country === "IN";
  const currentYear = fiscalYearOf(dayIn(companyTimezone, new Date()), india);
  // Any other year is offered only once the invoices say it has some.
  const held: FilterName[] = [
    ...(search.client && !clients ? (["client"] as const) : []),
    ...(!settings || (search.year !== undefined && search.year !== currentYear)
      ? (["year"] as const)
      : []),
  ];
  return (
    <PagePending
      shape="table"
      header={
        <>
          <InvoicesHeading
            owed={<HeldTitle className="text-body" lines={["w-64"]} />}
          />
          <InvoicesToolbar
            filters={
              <InvoiceFilters
                search={search}
                india={india}
                currentYear={currentYear}
                years={[currentYear]}
                clients={clients ?? []}
                open={open}
                onOpenChange={setOpen}
                onSearch={onSearch}
                held={held}
              />
            }
            q={search.q}
            onSearch={onSearch}
            newInvoice={
              <NewInvoiceForm busy={creating} plus onCreate={create} />
            }
          />
        </>
      }
    >
      <InvoiceTableSkeleton />
    </PagePending>
  );
}

/** Replaced so Back leaves Invoices instead of stepping through every filter. */
function useSearchChange() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (change: InvoiceListSearch) =>
    navigate({ search: { ...search, ...change }, replace: true });
}

/**
 * An empty draft with the company's default tax rows and note, opened at once; the draft screen
 * asks for the client (`docs/spec/money.md`).
 */
function useNewInvoice() {
  const { slug } = Route.useParams();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const [, create, creating] = useActionState(async () => {
    try {
      const draft = await unwrap(
        api.invoices.$post(
          {
            json: { clientId: null, lines: [] },
          },
          keyed(),
        ),
      );
      queryClient.setQueryData<InvoiceDetail>(
        invoiceQuery(draft.id).queryKey,
        draft,
      );
      queryClient.setQueryData<InvoiceListRow[]>(
        invoicesQuery.queryKey,
        (held) => held && [...held, draft],
      );
      newDrafts.set(draft.id, draft.note);
      await navigate({
        to: "/$slug/invoices/$invoiceId",
        params: { slug, invoiceId: draft.id },
      });
    } catch (failure) {
      fail(problemFor(failure));
    }
    return null;
  }, null);
  return [create, creating] as const;
}

function Invoices() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const { companyTimezone } = Route.useRouteContext();
  const now = useNow(TICK);
  const { data: rows } = useSuspenseQuery(invoicesQuery);
  const { data: clients } = useSuspenseQuery(clientsQuery);
  const { data: settings } = useSuspenseQuery(invoiceSettingsQuery);
  const [create, creating] = useNewInvoice();
  const onSearch = useSearchChange();

  return (
    <InvoicesPage
      slug={slug}
      rows={rows}
      clients={clients}
      search={search}
      today={dayIn(companyTimezone, now)}
      india={settings.country === "IN"}
      now={now}
      timeZone={companyTimezone}
      creating={creating}
      onCreate={create}
      onSearch={onSearch}
    />
  );
}
